import { useMemo } from "react";
import { z } from "zod";
import { GraphQL } from "@rpi/openapi-api";

export const ProductAttributesSchema = z.record(
  z.string(),
  z.string().or(z.number()).or(z.boolean()).or(z.undefined())
);

export type ProductAttributes = z.infer<typeof ProductAttributesSchema>;

// same as the graphql type but with validated (enforced flat string to scalar) attributes
export type StandardProduct = Omit<GraphQL.ListOpenApiProductsQuery["listOpenApiProducts"][number], "skuAttributes"> & {
  attributes: ProductAttributes;
};

export const useStandardProducts = () => {
  const { data, isLoading, isError, error } = GraphQL.useListOpenApiProductsQuery({
    hideAlreadyAssociatedProducts: false,
  });

  const standardProducts = useMemo(
    () =>
      data?.listOpenApiProducts
        .map<StandardProduct | null>((product) => {
          if (!product.skuAttributes) {
            return null;
          }

          return {
            ...product,
            attributes: ProductAttributesSchema.parse(product.skuAttributes),
          };
        })
        .filter((product): product is StandardProduct => product !== null),
    [data]
  );

  return { standardProducts, isLoading, isError, error };
};

export function filterByMatchingAttributes(products: StandardProduct[], requiredAttributes: ProductAttributes) {
  return products.filter((product) => {
    return Object.keys(requiredAttributes).every(
      // Compare as strings: SKU attributes are string-valued except textPrintMode, which can arrive as a number
      // (44/11) or a string depending on the backend/env, and strict === would miss across that drift.
      (key) =>
        requiredAttributes[key] === undefined || String(product.attributes[key]) === String(requiredAttributes[key])
    );
  });
}

// Distinct values for the key, normalised to strings so a mixed number/string response can't inflate the count
// or diverge from the string-keyed name maps, card ids, and combination dedupe.
export function findAvailableAttributeValues(products: StandardProduct[], attributeKey: string): string[] {
  return Array.from(
    new Set(
      products
        .map((product) => product.attributes[attributeKey])
        .filter((attributeValue): attributeValue is string | number | boolean => attributeValue !== undefined)
        .map(String)
    )
  );
}

// Stable id for a combination of attribute values, used to identify a card and dedupe combinations.
// Underscore is safe because attribute values are SKU components and the SKU format uses underscore as its separator.
export function serializeCombination(keys: string[], attributes: ProductAttributes): string {
  return keys.map((key) => String(attributes[key])).join("_");
}

// Distinct combinations of the given keys across the products, skipping products missing any of the keys.
export function findAvailableAttributeCombinations(products: StandardProduct[], keys: string[]): ProductAttributes[] {
  const combinations = new Map<string, ProductAttributes>();
  for (const product of products) {
    if (keys.some((key) => product.attributes[key] === undefined)) {
      continue;
    }
    const id = serializeCombination(keys, product.attributes);
    if (!combinations.has(id)) {
      combinations.set(id, Object.fromEntries(keys.map((key) => [key, product.attributes[key]])));
    }
  }
  return Array.from(combinations.values());
}
